import Box from '@liquidweb-dev/undercurrent/Box';
import Card, { type CardProps } from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ActionsMenu from 'components/standard/HostingDetailsActions/ActionsMenu';
import ServerStatusBadge from 'components/standard/ServerStatusBadge';
import ServerStatusBadgeFlat from 'components/standard/ServerStatusBadgeFlat';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import { Link as RouterLink } from 'react-router-dom';

export interface ServersListCardProps extends CardProps {
	assetDetails: AssetDetails;
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

const ServersListCard = ({
	assetDetails,
	notification,
	children,
	...rest
}: ServersListCardProps) => {
	const {
		custom_name: customName,
		domain,
		ip,
		isPrivateParent,
		path,
		powerStatus,
		status,
	} = assetDetails;
	const { last_alert: lastAlert, description } = notification || {};

	return (
		<Card
			component={RouterLink}
			to={path}
			sx={{
				color: 'initial',
				textDecoration: 'none',
				'&:hover': { boxShadow: ({ shadows }) => shadows[2] },
			}}
			{...rest}
		>
			<CardContent
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 200,
					position: 'relative',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						gap: 200,
						justifyContent: 'space-between',
						alignItems: 'start',
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							gap: 150,
							justifyContent: 'space-between',
							minWidth: 0,
							overflow: 'hidden',
							flexDirection: {
								xs: 'column',
								sm: 'row',
							},
							alignItems: {
								xs: 'start',
								sm: 'center',
							},
						}}
					>
						<ServerStatusBadge
							status={status}
							powerStatus={powerStatus}
							notificationPrimary={description}
							notificationSecondary={lastAlert || ''}
							sx={{
								position: 'absolute',
								maxWidth: '100%',
								display: {
									xs: 'none',
									sm: 'inline-flex',
								},
							}}
						/>

						<ServerStatusBadgeFlat
							status={status}
							powerStatus={powerStatus}
							notificationPrimary={description}
							notificationSecondary={lastAlert || ''}
							sx={{
								display: {
									sm: 'none',
								},
								maxWidth: '100%',
							}}
						/>

						<Stack
							sx={{
								gap: 100,
								minWidth: 0,
								maxWidth: '100%',
								marginLeft: { sm: '40px' },
							}}
						>
							<TypographyOverflow
								sx={{
									fontWeight: 500,
									typography: { xs: 'label-xl', sm: 'label-lg' },
								}}
							>
								{customName || domain}
							</TypographyOverflow>

							{!isPrivateParent && (
								<TypographyOverflow
									variant="label-md"
									sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
								>
									{ip}
								</TypographyOverflow>
							)}
						</Stack>
					</Box>

					<Box
						onClick={(e) => {
							// prevents all events from bubbling up to the list item button (which would cause the link to be followed)
							e.stopPropagation();
						}}
						sx={{ display: 'flex', alignItems: 'start' }}
					>
						<ActionsMenu
							{...assetDetails}
							intent="serversList"
							iconButtonProps={{
								onClick: (e) => {
									// for some reason also need to prevent default on the button to prevent the link from being followed
									e.preventDefault();
								},
								variant: 'ghost',
								size: 'small',
							}}
						/>
					</Box>
				</Box>

				<TypographyOverflow
					variant="label-md"
					sx={{ color: ({ vars }) => vars.palette.uc.text.weak }}
				>
					{domain}
				</TypographyOverflow>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 300,
					}}
				>
					{children}
				</Box>
			</CardContent>
		</Card>
	);
};

export default ServersListCard;
